/** PDR field metadata — mirrors orion1.pdr_profiles / phase3_schema. */

export const PDR_PROFILE_SLIM = "slim" as const;
export const PDR_PROFILE_COMPLETE = "complete" as const;
export type PdrSchemaProfile = typeof PDR_PROFILE_SLIM | typeof PDR_PROFILE_COMPLETE;

export const PDR_PROFILE_OPTIONS: { id: PdrSchemaProfile; label: string }[] = [
  { id: PDR_PROFILE_SLIM, label: "Slim (mandatory fields)" },
  { id: PDR_PROFILE_COMPLETE, label: "Complete (PDR Chapter 1)" },
];

export const PDR_SLIM_FIELD_KEYS = [
  "project_family",
  "project_name",
  "project_description",
  "project_risk_factors",
  "project_risk_level",
  "involved_systems_applications",
  "customer_facing_apps",
  "involved_organizations",
  "sector",
  "company_department",
  "project_announcement_date",
  "expected_end_date",
  "project_roadmap",
  "status",
  "likely_responsible_roles",
  "project_family_justification",
  "deal_score",
] as const;

export const PDR_COMPLETE_FIELD_KEYS = [
  "company_name",
  "company_department",
  "company_type",
  "ctpp_under_dora",
  "ctpp_parent_name",
  "project_owner",
  "parent_company",
  "project_theme",
  "project_family",
  "project_family_id",
  "project_family_justification",
  "project_family_justification_internal",
  "project_name",
  "project_description",
  "project_objective",
  "project_challenge",
  "project_risk_factors",
  "project_risk_level",
  "business_drivers",
  "project_found_date",
  "project_last_updated",
  "status",
  "investment_range",
  "budget",
  "likely_responsible_roles",
  "common_email_formats",
  "involved_organizations",
  "main_contractor",
  "involved_systems_applications",
  "customer_facing_apps",
  "sector",
  "industry",
  "country",
  "region",
  "project_announcement_date",
  "expected_end_date",
  "project_urgency",
  "sources",
  "source_type",
  "tender_reference_number",
  "related_regulation",
  "project_roadmap",
  "project_document",
  "project_tags",
  "deal_score",
  "deal_class",
  "project_accuracy_score",
  "project_accuracy_tier",
  "project_accuracy_justification",
  "project_accuracy_unverified_claims",
] as const;

/** Default table iteration (slim). */
export const PDR_FIELD_KEYS = PDR_SLIM_FIELD_KEYS;

export type PdrSlimFieldKey = (typeof PDR_SLIM_FIELD_KEYS)[number];
export type PdrCompleteFieldKey = (typeof PDR_COMPLETE_FIELD_KEYS)[number];
export type PdrFieldKey = PdrSlimFieldKey | PdrCompleteFieldKey;

const PLAIN_FIELD_KEYS = new Set<string>(["project_name"]);

export const REASONED_FIELD_KEYS = new Set<string>(
  [...PDR_COMPLETE_FIELD_KEYS, ...PDR_SLIM_FIELD_KEYS].filter(
    (k) => !PLAIN_FIELD_KEYS.has(k),
  ),
);

export const PDR_FIELD_LABELS: Record<string, string> = {
  company_name: "Company Name",
  company_department: "Company Department",
  company_type: "Company Type",
  ctpp_under_dora: "CTPP Under DORA",
  ctpp_parent_name: "CTPP Parent Name",
  project_owner: "Project Owner",
  parent_company: "Parent Company",
  project_theme: "Project Theme",
  project_family: "Project Family",
  project_family_id: "Project Family ID",
  project_family_justification: "Project Family Justification",
  project_family_justification_internal: "Project Family Justification (Internal)",
  project_name: "Project Name",
  project_description: "Project Description",
  project_objective: "Project Objective",
  project_challenge: "Project Challenge",
  project_risk_factors: "Project Risk Factor(s)",
  project_risk_level: "Project Risk Level",
  business_drivers: "Business Driver(s)",
  project_found_date: "Project Found Date",
  project_last_updated: "Project Last Updated",
  status: "Status",
  investment_range: "Investment Range",
  budget: "Budget",
  likely_responsible_roles: "Likely Responsible Roles",
  common_email_formats: "Common Email Formats",
  involved_organizations: "Involved Organization(s)",
  main_contractor: "Main Contractor",
  involved_systems_applications: "Involved Systems/Applications",
  customer_facing_apps: "Customer-Facing App(s)",
  sector: "Sector",
  industry: "Industry",
  country: "Country",
  region: "Region",
  project_announcement_date: "Project Announcement Date",
  expected_end_date: "Expected End Date",
  project_urgency: "Project Urgency",
  sources: "Sources",
  source_type: "Source Type",
  tender_reference_number: "Tender Reference Number",
  related_regulation: "Related Regulation",
  project_roadmap: "Project Roadmap",
  project_document: "Project Document",
  project_tags: "Project Tags",
  deal_score: "Deal Score (0-100)",
  deal_class: "Deal Class",
  project_accuracy_score: "Project Accuracy Score (0-100)",
  project_accuracy_tier: "Project Accuracy Tier",
  project_accuracy_justification: "Project Accuracy Justification",
  project_accuracy_unverified_claims: "Project Accuracy Unverified Claims",
};

/**
 * Runtime registry cache. The bundled constants above are the offline/default seed;
 * once the app fetches GET /api/pdr/fields (see PdrSchemaContext), `applyRegistry()`
 * swaps in the live, user-edited schema so labels/order/reasoned flags reflect edits.
 */
export type PdrFieldMeta = {
  key: string;
  label: string;
  reasoned: boolean;
  type: string;
  enum_values: string[];
  required: boolean;
  description?: string;
};

type PdrRuntime = { byKey: Map<string, PdrFieldMeta>; profiles: Map<string, string[]> };

function seedRuntime(): PdrRuntime {
  const byKey = new Map<string, PdrFieldMeta>();
  for (const key of new Set<string>([...PDR_COMPLETE_FIELD_KEYS, ...PDR_SLIM_FIELD_KEYS])) {
    byKey.set(key, {
      key,
      label: PDR_FIELD_LABELS[key] ?? key,
      reasoned: REASONED_FIELD_KEYS.has(key),
      type: key === "deal_score" || key === "project_accuracy_score" ? "integer" : "text",
      enum_values: [],
      required: true,
    });
  }
  const profiles = new Map<string, string[]>([
    [PDR_PROFILE_SLIM, [...PDR_SLIM_FIELD_KEYS]],
    [PDR_PROFILE_COMPLETE, [...PDR_COMPLETE_FIELD_KEYS]],
  ]);
  return { byKey, profiles };
}

let runtime: PdrRuntime = seedRuntime();

/** Swap in the live registry fetched from the backend (or reset to seed if empty). */
export function applyRegistry(
  reg: { fields?: PdrFieldMeta[]; profiles?: Record<string, string[]> } | null | undefined,
): void {
  if (!reg || !reg.fields || reg.fields.length === 0) {
    runtime = seedRuntime();
    return;
  }
  const byKey = new Map<string, PdrFieldMeta>();
  for (const f of reg.fields) {
    byKey.set(f.key, { ...f, enum_values: f.enum_values ?? [] });
  }
  const profiles = new Map<string, string[]>();
  for (const [p, keys] of Object.entries(reg.profiles ?? {})) {
    profiles.set(p, [...keys]);
  }
  if (!profiles.has(PDR_PROFILE_SLIM)) profiles.set(PDR_PROFILE_SLIM, [...PDR_SLIM_FIELD_KEYS]);
  if (!profiles.has(PDR_PROFILE_COMPLETE)) {
    profiles.set(PDR_PROFILE_COMPLETE, [...PDR_COMPLETE_FIELD_KEYS]);
  }
  runtime = { byKey, profiles };
}

/** Ordered field metadata for a profile (live registry, seed fallback). */
export function fieldMetaForProfile(profile?: string | null): PdrFieldMeta[] {
  return fieldKeysForProfile(profile)
    .map((k) => runtime.byKey.get(k))
    .filter((m): m is PdrFieldMeta => Boolean(m));
}

/** Primary desk fields for reasoning toggle / summary panels (slim). */
export const PDR_REASONING_DISPLAY_KEYS: PdrFieldKey[] = [
  "project_announcement_date",
  "expected_end_date",
  "deal_score",
  "status",
  "sector",
  "company_department",
  "project_description",
  "project_family",
  "involved_systems_applications",
  "involved_organizations",
  "project_roadmap",
  "project_risk_level",
  "likely_responsible_roles",
  "customer_facing_apps",
];

export function normalizePdrProfile(profile?: string | null): PdrSchemaProfile {
  return profile === PDR_PROFILE_COMPLETE ? PDR_PROFILE_COMPLETE : PDR_PROFILE_SLIM;
}

export function pdrProfileDisplayLabel(profile?: string | null): string {
  return normalizePdrProfile(profile) === PDR_PROFILE_COMPLETE ? "PDR Complete" : "PDR Slim";
}

export function fieldKeysForProfile(profile?: string | null): readonly string[] {
  const prof = normalizePdrProfile(profile);
  return (
    runtime.profiles.get(prof) ??
    (prof === PDR_PROFILE_COMPLETE ? PDR_COMPLETE_FIELD_KEYS : PDR_SLIM_FIELD_KEYS)
  );
}

export function fieldLabel(key: string): string {
  return runtime.byKey.get(key)?.label || PDR_FIELD_LABELS[key] || key;
}

export type ReasonedField = {
  value?: string | number | null;
  reasoning?: string;
  quote?: string;
  source_url?: string;
};

export type ProjectData = Partial<Record<string, string | ReasonedField>>;

export function isReasonedField(key: string): boolean {
  const meta = runtime.byKey.get(key);
  return meta ? meta.reasoned : REASONED_FIELD_KEYS.has(key);
}

export function isReasonedDict(val: unknown): val is ReasonedField {
  return typeof val === "object" && val !== null && "value" in val;
}

export function fieldValue(data: ProjectData, key: string): string {
  const raw = data[key];
  if (isReasonedDict(raw)) {
    const v = raw.value;
    if (v === null || v === undefined) return "";
    return String(v);
  }
  return raw != null ? String(raw) : "";
}

export function fieldReasoning(data: ProjectData, key: string): string {
  const raw = data[key];
  if (isReasonedDict(raw)) {
    return String(raw.reasoning || "").trim();
  }
  return "";
}

export function fieldQuote(data: ProjectData, key: string): string {
  const raw = data[key];
  if (isReasonedDict(raw)) {
    return String(raw.quote || "").trim();
  }
  return "";
}

export function fieldSourceUrl(data: ProjectData, key: string): string {
  const raw = data[key];
  if (isReasonedDict(raw)) {
    return String(raw.source_url || "").trim();
  }
  return "";
}

export function fieldHasContent(data: ProjectData, key: string): boolean {
  const v = fieldValue(data, key);
  const r = fieldReasoning(data, key);
  return Boolean(v.trim() || r);
}

/** Union of profile keys plus any extra keys present in result rows (historical runs). */
/** Empty project_data JSON for eval sandbox (reasoning shape). */
export function emptyPdrJsonSkeleton(
  profile: PdrSchemaProfile | string | null | undefined,
  includeReasoning = true,
): string {
  const prof = normalizePdrProfile(profile);
  const keys = fieldKeysForProfile(prof);
  const obj: Record<string, unknown> = {};
  for (const k of keys) {
    const meta = runtime.byKey.get(k);
    const reasoned = meta ? meta.reasoned : isReasonedField(k);
    const numeric = meta
      ? meta.type === "integer"
      : k === "deal_score" || k === "project_accuracy_score";
    if (!reasoned) {
      obj[k] = "";
      continue;
    }
    obj[k] = includeReasoning
      ? { value: numeric ? null : "", reasoning: "", quote: "", source_url: "" }
      : numeric
        ? null
        : "";
  }
  return JSON.stringify(obj, null, 2);
}

export function tableFieldKeys(
  profile: string | null | undefined,
  rows: { generated_pdr?: ProjectData | null }[],
): string[] {
  const base = [...fieldKeysForProfile(profile)];
  const seen = new Set(base);
  for (const row of rows) {
    const data = row.generated_pdr;
    if (!data) continue;
    for (const key of Object.keys(data)) {
      if (
        !seen.has(key) &&
        (runtime.byKey.has(key) || key in PDR_FIELD_LABELS || REASONED_FIELD_KEYS.has(key))
      ) {
        seen.add(key);
        base.push(key);
      }
    }
  }
  return base;
}
